import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Grid,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Storage as StorageIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Upgrade as UpgradeIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { useGetMyCompanyStorageQuery } from '../services/companyApi';
import { formatBytes } from '../utils/formatters';

const StorageMonitoringWidget = ({ onUpgradeClick }) => {
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  
  const { 
    data: storageData, 
    isLoading, 
    error, 
    refetch 
  } = useGetMyCompanyStorageQuery(undefined, {
    pollingInterval: 300000, // Refresh every 5 minutes
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" py={3}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading storage information...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            <AlertTitle>Storage Information Unavailable</AlertTitle>
            Unable to load storage information. Please try again later.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const { usage, plan, status } = storageData?.data || {};

  // Determine color and severity based on usage
  const getProgressColor = () => {
    if (status?.level === 'exceeded') return 'error';
    if (status?.level === 'warning') return 'warning';
    if (status?.level === 'caution') return 'info';
    return 'primary';
  };

  const getAlertSeverity = () => {
    if (status?.level === 'exceeded') return 'error';
    if (status?.level === 'warning') return 'warning';
    if (status?.level === 'caution') return 'info';
    return 'success';
  };

  const getStatusIcon = () => {
    switch (status?.level) {
      case 'exceeded':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'caution':
        return <InfoIcon color="info" />;
      default:
        return <CheckCircleIcon color="success" />;
    }
  };

  const getStatusMessage = () => {
    switch (status?.level) {
      case 'exceeded':
        return 'Storage limit exceeded! Data creation is blocked.';
      case 'warning':
        return 'Storage nearly full. Consider upgrading or cleaning up data.';
      case 'caution':
        return 'Storage usage is approaching the limit.';
      default:
        return 'Storage usage is within normal limits.';
    }
  };

  // Get all collections by size for breakdown
  const getAllCollections = () => {
    if (!usage?.collectionBreakdown) return [];

    return Object.entries(usage.collectionBreakdown)
      .filter(([, size]) => size > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([collection, size]) => ({
        name: collection,
        size: size,
        formatted: formatBytes(size),
        percentage: ((size / usage.totalBytes) * 100).toFixed(1)
      }));
  };

  const allCollections = getAllCollections();

  return (
    <>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <StorageIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              Storage Usage
            </Typography>
            <Box flexGrow={1} />
            {getStatusIcon()}
          </Box>

          {/* Usage Progress Bar */}
          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="textSecondary">
                {usage?.formattedSize || '0 B'} of {plan?.formattedLimit || 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {usage?.usagePercentage?.toFixed(1) || 0}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(usage?.usagePercentage || 0, 100)}
              color={getProgressColor()}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          {/* Plan Information */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="body2" color="textSecondary">
              Plan: {plan?.name || 'Unknown'}
            </Typography>
            <Chip 
              label={`${plan?.storageLimitGB || 0} GB`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>

          {/* Status Alert */}
          {status?.requiresAction && (
            <Alert severity={getAlertSeverity()} sx={{ mb: 2 }}>
              <AlertTitle>
                {status?.level === 'exceeded' ? 'Action Required' : 'Warning'}
              </AlertTitle>
              {getStatusMessage()}
            </Alert>
          )}

          {/* Action Buttons */}
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              startIcon={<InfoIcon />}
              onClick={() => setShowDetailsDialog(true)}
            >
              Details
            </Button>
            
            {status?.requiresAction && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<UpgradeIcon />}
                  onClick={onUpgradeClick}
                >
                  Upgrade Plan
                </Button>
                
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<DeleteIcon />}
                  onClick={() => setShowCleanupDialog(true)}
                >
                  Clean Up Data
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Storage Details Dialog */}
      <Dialog
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center">
            <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
              Storage Usage Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            background: 'linear-gradient(to bottom right, #fefefe 0%, #f1f3f4 100%)',
            p: 3,
          }}
        >
          <Grid container spacing={3}>
            {/* Usage Summary Card */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                  background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontWeight: 'bold',
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      mb: 3,
                    }}
                  >
                    <InfoIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                    Usage Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Total Used
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'semibold', color: 'primary.dark' }}>
                        {usage?.formattedSize || '0 B'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Plan Limit
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'semibold' }}>
                        {plan?.formattedLimit || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Usage %
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'semibold' }}>
                        {(usage?.usagePercentage?.toFixed(1) || 0) + '%'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Plan
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'semibold' }}>
                        {plan?.name || 'Unknown'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Storage Breakdown Card */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                  background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontWeight: 'bold',
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      mb: 3,
                    }}
                  >
                    <FolderIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                    Storage Breakdown
                  </Typography>
                  {allCollections.length > 0 ? (
                    <Box>
                      {allCollections.map((collection, index) => {
                        const percentage = parseFloat(collection.percentage);
                        const isHighUsage = percentage > 20;
                        return (
                          <Box
                            key={collection.name}
                            sx={{
                              mb: 2,
                              p: 2,
                              borderRadius: 2,
                              bgcolor: isHighUsage ? 'rgba(244, 67, 54, 0.04)' : 'grey.50',
                              border: isHighUsage ? '1px solid rgba(244, 67, 54, 0.2)' : '1px solid rgba(0,0,0,0.08)',
                            }}
                          >
                            <Box display="flex" alignItems="center" mb={1}>
                              <FolderIcon sx={{ mr: 1, fontSize: '1rem', color: isHighUsage ? 'error.main' : 'primary.main' }} />
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 'medium',
                                  color: isHighUsage ? 'error.main' : 'text.primary',
                                }}
                              >
                                {collection.name.charAt(0).toUpperCase() + collection.name.slice(1)}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <Typography variant="body2" color="text.secondary">
                                {collection.formatted}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {collection.percentage}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={percentage}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: isHighUsage ? '#ff6b6b !important' : '#4caf50 !important',
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                      }}
                    >
                      <InfoIcon sx={{ fontSize: '3rem', color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No data breakdown available
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Storage usage information is not detailed
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setShowDetailsDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Close
          </Button>
          <Button onClick={() => refetch()} variant="contained" sx={{ borderRadius: 2, ml: 1 }}>
            Refresh Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Data Cleanup Dialog */}
      <Dialog 
        open={showCleanupDialog} 
        onClose={() => setShowCleanupDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <DeleteIcon sx={{ mr: 1 }} />
            Data Cleanup Options
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Storage Management</AlertTitle>
            You can free up space by removing old or unnecessary data. Please be careful as this action cannot be undone.
          </Alert>
          
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Suggested cleanup actions:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText 
                primary="Archive old attendance records" 
                secondary="Move attendance data older than 2 years to archive"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Clean up old payroll files" 
                secondary="Remove generated payroll files older than 7 years"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Remove duplicate records" 
                secondary="Identify and remove any duplicate employee or attendance records"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Compress document storage" 
                secondary="Optimize stored documents and files"
              />
            </ListItem>
          </List>
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            Data cleanup features are coming soon. For immediate assistance, please contact support.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCleanupDialog(false)}>
            Close
          </Button>
          <Button variant="outlined" disabled>
            Contact Support
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StorageMonitoringWidget;
